'use client';

import { AppLink as Link } from '@/lib/navigation';
import { ArrowLeft, Calendar, CalendarCheck, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ProcessInstanceIdCopy } from '@/components/process-instance-id-copy';
import { Button } from '@/components/ui/button';
import { getHistoricProcessStatePresentation } from '@/lib/camunda/history-process-state';
import type { HistoricProcessInstance } from '@/lib/camunda/history-types';
import { formatDuration } from '@/lib/format-duration';
import { cn } from '@/lib/utils';
import { HistoryMetadataItem } from './history-metadata-item';
import type { HistoryAccessReason } from '@/lib/camunda/history-access';

interface ProcessHistoryHeaderProps {
  process: HistoricProcessInstance;
  accessReasons: HistoryAccessReason[];
}

export function ProcessHistoryHeader({ process, accessReasons }: ProcessHistoryHeaderProps) {
  const { label, className } = getHistoricProcessStatePresentation(process.state);
  const processName = process.processDefinitionName || process.processDefinitionKey;

  return (
    <div className="flex flex-col gap-2">
      <Button variant="ghost" size="sm" className="-ml-2 w-fit px-2" asChild>
        <Link href="/task-list/participated">
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Volver al histórico
        </Link>
      </Button>

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <h1 className="min-w-0 text-xl font-semibold leading-tight">{processName}</h1>
          <Badge variant="outline" className={cn('shrink-0', className)}>
            {label}
          </Badge>
          {accessReasons.includes('LABORAL_SUPERVISION') ? (
            <Badge variant="outline" className="shrink-0 border-amber-200 bg-amber-50 text-amber-800">
              Vista de supervisión Laboral
            </Badge>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <HistoryMetadataItem
              icon={<Calendar className="size-3.5" />}
              label="Inicio"
              value={new Date(process.startTime).toLocaleString()}
            />
            {process.endTime ? (
              <HistoryMetadataItem
                icon={<CalendarCheck className="size-3.5" />}
                label="Fin"
                value={new Date(process.endTime).toLocaleString()}
              />
            ) : null}
            {process.durationInMillis != null ? (
              <HistoryMetadataItem
                icon={<Clock className="size-3.5" />}
                label="Duración"
                value={formatDuration(process.durationInMillis)}
              />
            ) : null}
          </div>

          <ProcessInstanceIdCopy
            processInstanceId={process.id}
            className="sm:shrink-0"
            textClassName="max-w-[min(48vw,34rem)] leading-none text-gray-500"
          />
        </div>
      </div>
    </div>
  );
}
